import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, BarChart3, Save, ExternalLink, CheckCircle2, AlertCircle } from "lucide-react";

const FIELDS_CONV = [
  { key: "form", label: "Formulaire envoyé", help: "Conversion principale (lead)" },
  { key: "call", label: "Clic téléphone", help: "Quand un visiteur clique sur tel:" },
  { key: "email", label: "Clic email", help: "Quand un visiteur clique sur mailto:" },
  { key: "visit_request", label: "Demande de visite", help: "Réservation / demande de visite envoyée" },
  { key: "listing_view", label: "Consultation d'annonce", help: "Optionnel — vue d'une fiche locale" },
  { key: "document_download", label: "Téléchargement document", help: "Optionnel — téléchargement de plaquette / PDF" },
];

export default function TrackingAdmin() {
  const [data, setData] = useState({
    ga4_id: "",
    gtm_id: "",
    gads_conversion_id: "",
    gads_labels: { form: "", call: "", email: "", visit_request: "", listing_view: "", document_download: "" },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get("/site-content/tracking");
        if (r && r.value) {
          setData(prev => ({
            ...prev,
            ...r.value,
            gads_labels: { ...prev.gads_labels, ...(r.value.gads_labels || {}) },
          }));
        }
      } catch (e) {
        // Empty config means tracking has never been set up — first-time admin
        console.debug("[TrackingAdmin] No tracking config yet:", e?.response?.status);
      }
      finally { setLoading(false); }
    })();
  }, []);

  const setField = (k, v) => setData(prev => ({ ...prev, [k]: v }));
  const setLabel = (k, v) => setData(prev => ({ ...prev, gads_labels: { ...prev.gads_labels, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-content/tracking", { value: data });
      toast.success("Configuration de tracking enregistrée. Rechargez la page pour activer les scripts.");
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const ok = (s) => s && s.trim().length > 0;
  const hasGtm = ok(data.gtm_id);
  const hasGa4 = ok(data.ga4_id);
  const hasAds = ok(data.gads_conversion_id);

  return (
    <div className="min-h-screen bg-[color:var(--bg-default)]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <div className="max-w-5xl mx-auto px-6 md:px-8 py-10">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 mb-6"><ArrowLeft className="w-4 h-4"/>Retour au dashboard</Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--solar)] mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4"/>Tracking & Analytics</div>
            <h1 className="font-display font-black text-3xl md:text-5xl tracking-tight">Configuration GA4 / GTM / Google Ads</h1>
            <p className="text-slate-600 mt-2 max-w-2xl text-sm">Collez ici les identifiants de vos comptes Google. Les scripts seront injectés automatiquement sur tout le site. Aucun redéploiement nécessaire.</p>
          </div>
          <button onClick={save} disabled={saving || loading} data-testid="save-tracking-btn"
            className="btn-brutal btn-brutal-solar inline-flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-50">
            <Save className="w-4 h-4"/>{saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>

        {loading ? (
          <div className="text-slate-500">Chargement…</div>
        ) : (
          <div className="space-y-8">
            {/* Status row */}
            <div className="grid md:grid-cols-3 gap-3">
              <StatusBadge ok={hasGtm} label="Google Tag Manager" value={data.gtm_id || "—"} />
              <StatusBadge ok={hasGa4} label="GA4" value={data.ga4_id || "—"} />
              <StatusBadge ok={hasAds} label="Google Ads" value={data.gads_conversion_id || "—"} />
            </div>

            {/* GTM */}
            <Section title="1. Google Tag Manager (recommandé)"
              help="Si vous utilisez GTM, configurez GA4 et Google Ads à l'intérieur de GTM. Il prendra la priorité sur GA4 ci-dessous. Créez votre container sur tagmanager.google.com."
              docLink="https://tagmanager.google.com">
              <Field label="GTM Container ID" placeholder="GTM-XXXXXXX" value={data.gtm_id} onChange={v => setField("gtm_id", v)} testid="gtm-id" />
            </Section>

            {/* GA4 */}
            <Section title="2. Google Analytics 4"
              help={hasGtm ? "Si GTM est configuré, laissez ce champ vide et créez votre tag GA4 dans GTM." : "Si vous n'utilisez pas GTM, renseignez votre Measurement ID GA4. Créez-le sur analytics.google.com → Admin → Flux de données Web."}
              docLink="https://analytics.google.com">
              <Field label="GA4 Measurement ID" placeholder="G-XXXXXXXXXX" value={data.ga4_id} onChange={v => setField("ga4_id", v)} testid="ga4-id" />
            </Section>

            {/* Google Ads */}
            <Section title="3. Google Ads — Conversions"
              help="Créez d'abord vos conversions dans Google Ads → Outils → Conversions. Pour chaque conversion, copiez l'ID (AW-...) et le Label (la chaîne après le slash)."
              docLink="https://ads.google.com">
              <Field label="Conversion ID" placeholder="AW-XXXXXXXXX" value={data.gads_conversion_id} onChange={v => setField("gads_conversion_id", v)} testid="gads-id" />
              <div className="mt-6">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Labels de conversion (un par événement à mesurer)</div>
                <div className="grid md:grid-cols-2 gap-4">
                  {FIELDS_CONV.map(f => (
                    <Field
                      key={f.key}
                      label={f.label}
                      placeholder="abcDEF123_xyz"
                      help={f.help}
                      value={data.gads_labels?.[f.key] || ""}
                      onChange={v => setLabel(f.key, v)}
                      testid={`gads-label-${f.key}`}
                    />
                  ))}
                </div>
              </div>
            </Section>

            {/* Events overview */}
            <Section title="4. Événements suivis automatiquement"
              help="Voici les événements envoyés par le site dès qu'un visiteur déclenche l'action correspondante. Aucune action requise de votre part.">
              <ul className="text-sm space-y-2 text-slate-700">
                <Evt name="page_view" desc="Toutes les pages (Single Page App)" />
                <Evt name="form_submit" desc="Contact, demande personnalisée, formulaire dynamique" />
                <Evt name="phone_click" desc="Clic sur un numéro de téléphone (header / footer / contact)" />
                <Evt name="email_click" desc="Clic sur un lien email" />
                <Evt name="view_item" desc="Consultation d'une annonce (PropertyDetail)" />
                <Evt name="visit_request" desc="Demande de visite / réservation envoyée" />
                <Evt name="file_download" desc="Téléchargement de document" />
              </ul>
              <p className="text-xs text-slate-500 mt-4">Tous les événements sont poussés dans <code className="bg-slate-100 px-1 py-0.5">window.dataLayer</code> (GTM) et envoyés via <code className="bg-slate-100 px-1 py-0.5">gtag()</code> quand GA4 est en direct.</p>
            </Section>

            {/* Save bottom */}
            <div className="flex justify-end">
              <button onClick={save} disabled={saving} data-testid="save-tracking-btn-bottom"
                className="btn-brutal btn-brutal-solar inline-flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-50">
                <Save className="w-4 h-4"/>{saving ? "Enregistrement…" : "Enregistrer la configuration"}
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const Section = ({ title, help, docLink, children }) => (
  <div className="bg-white border border-slate-900/15 p-6 md:p-8">
    <div className="flex items-start justify-between gap-4 mb-2">
      <h2 className="font-display font-bold text-xl">{title}</h2>
      {docLink && <a href={docLink} target="_blank" rel="noreferrer" className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900 flex items-center gap-1"><ExternalLink className="w-3 h-3"/>Ouvrir</a>}
    </div>
    {help && <p className="text-sm text-slate-600 mb-4">{help}</p>}
    {children}
  </div>
);

const Field = ({ label, value, onChange, placeholder, help, testid }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">{label}</span>
    <input value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="input w-full font-mono" data-testid={testid} />
    {help && <span className="text-xs text-slate-500 mt-1 block">{help}</span>}
  </label>
);

const StatusBadge = ({ ok, label, value }) => (
  <div className={`border p-4 flex items-center gap-3 ${ok ? "border-emerald-500/40 bg-emerald-50" : "border-slate-300 bg-slate-50"}`}>
    {ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600"/> : <AlertCircle className="w-5 h-5 text-slate-400"/>}
    <div className="min-w-0">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-sm font-mono truncate ${ok ? "text-emerald-700" : "text-slate-500"}`}>{value}</div>
    </div>
  </div>
);

const Evt = ({ name, desc }) => (
  <li className="flex items-start gap-3"><code className="bg-slate-900 text-white px-2 py-0.5 text-xs font-mono shrink-0">{name}</code><span>{desc}</span></li>
);
